import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { v4 } from 'uuid'
import { TemplateSelect } from 'v2/pages/conversations/outbound/components/template-select'
import {
  VariableEditor,
  type WhatsAppFormType,
} from 'v2/pages/conversations/outbound/components/variable-editor'
import { useWhatsAppTemplatesQuery } from 'v2/pages/settings/whatsapp/templates/queries'
import type { FileMimeType, RFileUploadResponse } from 'v2/types/api/auth'
import z from 'zod'
import { MediaUpload } from '../components/media-upload/media-upload'
import { NodevarList } from '../components/nodevar-list'
import { v2validations } from '../config/validation.config'
import type { SendWhatsappNodeData, SidebarComponentProps } from '../types'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = SendWhatsappNodeData['config']

const fields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).send_whatsapp
  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
      inputProps: {
        type: 'text',
        placeholder: 'Enter your name',
      },
    },
    {
      name: 'from',
      label: 'Send WhatsApp from',
      zodValidation: validations.shape.from,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter',
        endIcon: (
          <NodevarList
            nodevars={fieldsWithDynamicNodeVars.from}
            className="pr-2"
            inputElementSelector="[name=from]"
          />
        ),
      },
    },
    {
      name: 'to',
      label: 'Send WhatsApp to',
      zodValidation: validations.shape.to,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter',
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=to]" />
        ),
      },
    },
    {
      name: 'whatsapp_msg_type',
      label: 'WhatsApp message type',
      zodValidation: validations.shape.whatsapp_msg_type,
      componentType: 'radioGroup',
      inputProps: {
        options: [
          { label: 'Custom Message', value: 'custom' },
          { label: 'WhatsApp Template', value: 'template' },
        ],
      },
    },
    {
      name: 'text',
      label: 'Text',
      zodValidation: (values) => {
        if (values.whatsapp_msg_type !== 'custom') return z.string().optional()

        const hasAudioOrDoc = values.whatsapp_media?.some(
          (media) =>
            media.media_type?.includes('audio') || media.media_type?.includes('application'),
        )

        if (hasAudioOrDoc)
          return z.string().max(0, 'Text must be empty when audio or document is uploaded')

        return z.string().min(1, 'Text is required when whatsapp message type is custom')
      },
      componentType: 'textarea',
      onChangeListenTo: ['whatsapp_msg_type'],
      visible: (values) => values.whatsapp_msg_type === 'custom',
      inputProps: {
        placeholder: 'Enter text here',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            sideOffset={40}
            className="pr-2"
            inputElementSelector="[name=text]"
          />
        ),
      },
    },
    {
      name: 'whatsapp_media',
      type: 'custom',
      label: 'Upload media (optional)',
      zodValidation: validations.shape.whatsapp_media,
      onChangeListenTo: ['whatsapp_msg_type', 'text'],
      visible: (values) => values.whatsapp_msg_type === 'custom',
      eventDataExtractor: (data: Record<string, unknown>) => data,
      component: ({
        form,
        onChange,
      }: {
        form: GenericFormType<SendWhatsappNodeData['config']>
        onChange: (value: Record<string, unknown>[]) => void
      }) => {
        const onUrlChange = (uploadedFile: RFileUploadResponse) => {
          const mediaFiles = {
            media_type: uploadedFile.file_type,
            name: uploadedFile.file_name,
            url: uploadedFile.file_url,
            size: uploadedFile.file_size,
            file_id: uploadedFile.file_id,
          }
          onChange([...form.state.values.whatsapp_media, mediaFiles])
        }

        const onRemove = (fileToRemove: RFileUploadResponse) => {
          onChange(
            form.state.values.whatsapp_media.filter((file) => file.url !== fileToRemove.file_url),
          )
        }
        let initialFiles: RFileUploadResponse[] = []
        if (form.state.values.whatsapp_media) {
          initialFiles = form.state.values.whatsapp_media.map((file) => ({
            file_type: file.media_type as FileMimeType,
            file_name: file.name || '',
            file_url: file.url || '',
            file_size: file.size || 0,
            file_id: file.file_id || v4(),
          }))
        }
        return (
          <MediaUpload
            onURLChange={onUrlChange}
            onRemove={onRemove}
            label="Upload media (optional)"
            initialFiles={initialFiles}
            nodeVars={nodeVars}
            disableFiles={(form.state.values.text?.length ?? 0) > 0 ? ['audio', 'application'] : []}
          />
        )
      },
    },
    {
      name: 'template',
      zodValidation: (values) =>
        values.whatsapp_msg_type === 'template'
          ? z.record(z.any()).refine((obj) => Object.keys(obj).length > 0, {
              message: 'Template is required when whatsapp message type is template',
            })
          : z.string().optional(),
      visible: (values) => values.whatsapp_msg_type === 'template',
      onChangeListenTo: ['whatsapp_msg_type', 'from'],
      component: ({ form }) => (
        <TemplateSelect
          showApprovedOnly
          selectedSender={form.state.values.from}
          form={form as unknown as GenericFormType<WhatsAppFormType>}
        />
      ),
      type: 'custom',
    },
    {
      name: 'variables',
      zodValidation: z.any(),
      className: 'overflow-hidden',
      visible: (values) =>
        values.whatsapp_msg_type === 'template' && Object.keys(values.template).length > 0,
      onChangeListenTo: ['whatsapp_msg_type'],
      component: ({ form }) => (
        <VariableEditor
          form={form as unknown as GenericFormType<WhatsAppFormType>}
          value={form.state.values.variables}
          nodeVars={nodeVars}
        />
      ),
      type: 'custom',
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const SendWhatsappSidebarComponent = (
  props: SidebarComponentProps<SendWhatsappNodeData>,
) => {
  const { nodeVars } = props
  const { data: templatesResponse } = useWhatsAppTemplatesQuery()
  const senderIds = templatesResponse?.map((template) => template.sender_ids)
  const fieldsWithDynamicNodeVars = {
    from: [...(senderIds?.flat() || []), ...nodeVars],
  }
  const formFields = fields(nodeVars, fieldsWithDynamicNodeVars)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
